import { Fragment } from "react";
import { CHECKLIST_POINTS } from "./checklistData";
import { fuelPlans, computeWB, taxiFuelL } from "@/lib/navcalc";
import { fmt, minToHm } from "@/lib/units";

// Printable briefing (dark-on-light). Shown only in @media print (.print-area).
// mode: 'full' | 'condensed'
export default function PrintView({ briefing, aircraft, nav, mode }) {
  const burn = Number(aircraft?.fuelBurn) || 0;
  const plans = fuelPlans(briefing, nav, aircraft);
  const wb = computeWB(aircraft, briefing.weights || {}, Number(briefing.fuelStartL) || 0, taxiFuelL(briefing.fuel, burn), nav.totalFuel);
  const sections = nav.ret ? [["Aller", nav.out], ["Retour", nav.ret]] : [["", nav.out]];

  const th = { border: "1px solid #333", padding: "4px 6px", background: "#eee", fontSize: 10, textAlign: "left" };
  const td = { border: "1px solid #999", padding: "4px 6px", fontSize: 10 };

  return (
    <div className="print-area" style={{ color: "#000", background: "#fff", padding: 24, fontFamily: "Arial, sans-serif" }}>
      {/* Header */}
      <div className="print-page">
        <div style={{ borderBottom: "3px solid #000", paddingBottom: 8, marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>Dossier de navigation VFR — {briefing.name}</h1>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            {briefing.departure || "?"} → {briefing.arrival || "?"} · Avion : {aircraft?.registration} ({aircraft?.model})
          </div>
          <div style={{ fontSize: 12, color: "#444", marginTop: 2 }}>
            {fmt(nav.totalDistance)} NM · {minToHm(nav.totalTime)} · {fmt(nav.totalFuel, 1)} L · TAS {aircraft?.tasCruise} kt
          </div>
        </div>

        <h2 style={{ fontSize: 15, marginBottom: 6 }}>1. Log de navigation</h2>
        {sections.map(([title, sec]) => (
        <Fragment key={title}>
        {title && <div style={{ fontSize: 12, fontWeight: 700, margin: "4px 0" }}>{title}</div>}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
          <thead>
            <tr>
              {["Point", "Alt", "Rv°", "Décl", "Vent", "Cap vrai", "Cap mag", "Cap comp", "Dist", "Vsol", "Temps", "Cumul", "Carbu"].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sec.rows.map((r, i) => (
              <Fragment key={i}>
              <tr>
                <td style={td}>{r.name || `Pt ${i + 1}`}</td>
                <td style={td}>{fmt(r.altitude)}</td>
                <td style={td}>{fmt(r.trackTrue)}°</td>
                <td style={td}>{fmt(r.variation, 1)}</td>
                <td style={td}>{fmt(r.windDir)}/{fmt(r.windSpeed)}</td>
                <td style={{ ...td, fontWeight: 700 }}>{fmt(r.headingTrue)}°</td>
                <td style={{ ...td, fontWeight: 700 }}>{fmt(r.headingMag)}°</td>
                <td style={td}>{fmt(r.headingCompass)}°</td>
                <td style={td}>{fmt(r.distance)}</td>
                <td style={td}>{fmt(r.gs)}</td>
                <td style={td}>{fmt(r.timeMin)}′</td>
                <td style={td}>{minToHm(r.cumTime)}</td>
                <td style={td}>{fmt(r.fuel, 1)} L</td>
              </tr>
              {(r.frequencies || []).length > 0 && (
                <tr>
                  <td style={{ ...td, fontSize: 9, background: "#f6f6f6" }} colSpan={13}>
                    <b>Fréquences :</b> {r.frequencies.map((f) => `${f.type} ${f.name || ""} ${f.freq || ""}`.replace(/\s+/g, " ").trim()).join("  ·  ")}
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
            <tr>
              <td style={{ ...td, fontWeight: 700 }} colSpan={8}>TOTAUX</td>
              <td style={{ ...td, fontWeight: 700 }}>{fmt(sec.totalDistance)}</td>
              <td style={td}></td>
              <td style={{ ...td, fontWeight: 700 }}>{minToHm(sec.totalTime)}</td>
              <td style={td}></td>
              <td style={{ ...td, fontWeight: 700 }}>{fmt(sec.totalFuel, 1)} L</td>
            </tr>
          </tbody>
        </table>
        </Fragment>
        ))}

        {/* Fuel + WB side by side */}
        <div style={{ display: "flex", gap: 24 }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 15, marginBottom: 6 }}>2. Bilan carburant</h2>
            {plans.map(({ title, plan }) => (
            <table key={title} style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
              {title && <thead><tr><th style={th} colSpan={3}>{title}</th></tr></thead>}
              <tbody>
                {[
                  ["Forfait roulage", plan.taxi], ["Voyage", plan.trip], ["Marge aléas", plan.contingency],
                  ["Forfait procédure", plan.procedure], ["Alternative déroutement", plan.alternate],
                  ["Réserve réglementaire VFR", plan.reserve], ["Carburant non utilisable", plan.unusable],
                  ["Minimum réglementaire", plan.minRequired], ["Réserve exploitant CDB", plan.cdb],
                  ["Total requis", plan.totalRequired], ["Embarqué", plan.onboard], ["Marge", plan.margin],
                ].map(([l, v]) => (
                  <tr key={l}>
                    <td style={td}>{l}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: l === "Marge" ? 700 : 400 }}>{fmt(v, 1)} L</td>
                    <td style={{ ...td, textAlign: "right", color: "#555" }}>{minToHm(burn > 0 ? (v / burn) * 60 : 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            ))}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 15, marginBottom: 6 }}>3. Masse & centrage</h2>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th style={th}>Phase</th><th style={th}>Masse</th><th style={th}>CG</th><th style={th}>État</th></tr>
              </thead>
              <tbody>
                {[["Rampe", wb.ramp], ["Décollage", wb.takeoff], ["Atterrissage", wb.landing]].map(([l, s]) => (
                  <tr key={l}>
                    <td style={td}>{l}</td>
                    <td style={td}>{fmt(s.weight)} kg</td>
                    <td style={td}>{fmt(s.cg, 3)} m</td>
                    <td style={td}>{s.ok ? "OK" : "HORS LIMITE"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 10, marginTop: 4 }}>MTOW : {wb.mtow} kg</div>
          </div>
        </div>
      </div>

      {/* Checklist pages (full mode only) */}
      {mode === "full" && (
        <div>
          <h2 style={{ fontSize: 15, marginBottom: 6 }}>4. Checklist briefing (11 points)</h2>
          {CHECKLIST_POINTS.map((p) => {
            const state = briefing.checklist?.[p.id] || {};
            return (
              <div key={p.id} style={{ marginBottom: 10, borderLeft: "3px solid #0a7", paddingLeft: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 12 }}>
                  {p.id}. {p.title} — {state.done ? "✓ vérifié" : "à faire"}
                </div>
                <div style={{ fontSize: 10, color: "#333" }}>{p.purpose}</div>
                {(p.fields || []).map((f) =>
                  state[f.key] ? (
                    <div key={f.key} style={{ fontSize: 10, marginTop: 2 }}>
                      <b>{f.label} :</b> {state[f.key]}
                    </div>
                  ) : null
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 20, fontSize: 9, color: "#666", borderTop: "1px solid #ccc", paddingTop: 6 }}>
        VFR Nav Organizer — outil d'aide à la préparation. Ne se substitue pas aux sources réglementaires officielles.
        Vérifiez toutes les valeurs (perfos, météo, NOTAM) auprès des sources officielles.
      </div>
    </div>
  );
}
